import React, { useEffect, useState } from "react";
import { BellRing, Check } from "lucide-react";
import { toast } from "sonner";
import { getDealAlerts, saveDealAlerts } from "../../lib/api";

function Toggle({ checked, onChange, label, hint, testId, disabled }) {
  return (
    <label className={`flex items-start gap-3 text-sm ${disabled ? "opacity-50" : ""}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 accent-espresso" data-testid={testId} />
      <span><span className="text-ink">{label}</span>{hint && <span className="block text-[11px] text-ink/50">{hint}</span>}</span>
    </label>
  );
}

export const DealAlertsCard = () => {
  const [prefs, setPrefs] = useState(null);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { getDealAlerts().then((p) => { setPrefs(p); setPhone(p.phone || ""); }).catch(() => setPrefs(null)); }, []);
  if (!prefs) return null;

  const toggleSuburb = (s) => setPrefs((p) => ({ ...p, suburbs: p.suburbs.includes(s) ? p.suburbs.filter((x) => x !== s) : [...p.suburbs, s] }));
  const save = async () => {
    setSaving(true);
    try {
      const next = await saveDealAlerts({ suburbs: prefs.suburbs, email: prefs.email, sms: prefs.sms, phone: phone.trim() || null });
      setPrefs(next);
      setPhone(next.phone || "");
      toast.success(next.suburbs.length ? `Alerts on for ${next.suburbs.join(", ")}.` : "Deal alerts turned off.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your alerts.");
    } finally { setSaving(false); }
  };

  return (
    <section className="mt-10 border border-sand/80 rounded-2xl bg-bone p-6" data-testid="deal-alerts-card">
      <div className="flex items-center gap-2"><BellRing size={16} className="text-mocha" /><h2 className="font-serif text-2xl text-ink">Nearby deal alerts</h2></div>
      <p className="mt-1 text-sm text-ink/60">Pick your favourite suburbs. When a salon there posts a last-minute deal, we&rsquo;ll let you know straight away.</p>
      <div className="mt-5 flex flex-wrap gap-2" data-testid="deal-alerts-suburbs">
        {prefs.available_suburbs.map((s) => {
          const on = prefs.suburbs.includes(s);
          return (
            <button key={s} type="button" onClick={() => toggleSuburb(s)} aria-pressed={on} className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs transition-colors ${on ? "bg-espresso text-bone border-espresso" : "border-sand text-ink/70 hover:border-espresso"}`} data-testid={`deal-alerts-suburb-${s.replace(/\s+/g, "-").toLowerCase()}`}>
              {on && <Check size={11} />}{s}
            </button>
          );
        })}
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <Toggle checked={prefs.email} onChange={(v) => setPrefs((p) => ({ ...p, email: v }))} label="Email me" hint={`Up to ${prefs.daily_cap} alert emails a day`} testId="deal-alerts-email" />
        <Toggle checked={prefs.sms} onChange={(v) => setPrefs((p) => ({ ...p, sms: v }))} label="Text me" hint={`Up to ${prefs.daily_cap} texts a day · reply STOP to opt out`} testId="deal-alerts-sms" />
      </div>
      {prefs.sms && (
        <label className="mt-4 block max-w-xs">
          <span className="block text-xs text-ink/60 mb-1.5">Mobile for texts</span>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0412 345 678" className="w-full border border-sand rounded-md px-3 py-2.5 outline-none focus:border-espresso text-[15px] bg-bone" data-testid="deal-alerts-phone" />
        </label>
      )}
      <p className="mt-4 text-[11px] text-ink/45">New deals always appear in your notifications bell too.</p>
      <button type="button" onClick={save} disabled={saving} className="mt-5 rounded-full bg-espresso text-bone px-6 py-2.5 text-sm hover:bg-ink transition-colors disabled:opacity-60" data-testid="deal-alerts-save">{saving ? "Saving…" : "Save alerts"}</button>
    </section>
  );
};
